export const formatRupiah = (value: number): string => {
  const abs = Math.abs(value);
  const formatted = new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(abs);
  return value < 0 ? `-${formatted}` : formatted;
};

export const formatDate = (dateStr: string): string => {
  return dateStr;
};

export type ProjectStatus = 'Berjalan' | 'Persiapan' | 'Ditunda' | 'Selesai' | 'Dibatalkan';
export type HutangStatus = 'Belum Lunas' | 'Sebagian' | 'Lunas' | 'Overdue' | '1-7 Hari' | 'Belum Jatuh Tempo';
export type ApprovalStatus = 'Pending' | 'Approved' | 'Rejected';
export type KategoriHutang = 'Material' | 'Subkon' | 'Upah' | 'Sewa Alat' | 'Operasional' | 'Konsultan' | 'Lainnya';
export type SumberDana = 'Donasi' | 'Wakaf' | 'Internal' | 'Hibah' | 'Lainnya';
export type MetodePembayaran = 'Transfer' | 'Cash' | 'QRIS' | 'Lainnya';
export type BankKas = 'Kas Proyek' | 'BSI' | 'Bank Lain';

export interface Project {
  id: string;
  nama: string;
  lokasi: string;
  pic: string;
  manager: string;
  tanggalMulai: string;
  targetSelesai: string;
  budget: number;
  status: ProjectStatus;
  deskripsi: string;
}

export interface SubProject {
  id: string;
  projectId: string;
  nama: string;
  pic: string;
  budget: number;
  status: ProjectStatus;
}

export interface Hutang {
  id: string;
  projectId: string;
  subProjectId: string;
  tanggal: string;
  vendor: string;
  kategori: KategoriHutang;
  uraian: string;
  noInvoice: string;
  nilaiTagihan: number;
  totalDibayar: number;
  sisaHutang: number;
  jatuhTempo: string;
  umurHari: number;
  status: HutangStatus;
  pic: string;
  approval: string;
  lampiran: string;
  approvalStatus: ApprovalStatus;
  approvalComment?: string;
  approvedBy?: string;
  approvedAt?: string;
}

export interface PembayaranHutang {
  id: string;
  hutangId: string;
  projectId: string;
  tanggalPembayaran: string;
  vendor: string;
  nominal: number;
  bankKas: BankKas;
  metode: MetodePembayaran;
  noBukti: string;
  lampiran: string;
}

export interface Piutang {
  id: string;
  projectId: string;
  subProjectId: string;
  tanggal: string;
  sumberDana: SumberDana;
  pihak: string;
  uraian: string;
  nilaiPiutang: number;
  totalDiterima: number;
  sisaPiutang: number;
  jatuhTempo: string;
  umurHari: number;
  status: HutangStatus;
  pic: string;
  lampiran: string;
  approvalStatus: ApprovalStatus;
  approvalComment?: string;
  approvedBy?: string;
  approvedAt?: string;
}

export interface PenerimaanPiutang {
  id: string;
  piutangId: string;
  projectId: string;
  tanggalPenerimaan: string;
  pihak: string;
  nominal: number;
  bankKas: BankKas;
  metode: MetodePembayaran;
  noBukti: string;
  lampiran: string;
}

// ─── localStorage helpers ────────────────────────────────────────────────────

const LS_KEYS = {
  projects: 'hp_projects',
  subProjects: 'hp_subProjects',
  hutang: 'hp_hutang',
  pembayaran: 'hp_pembayaran',
  piutang: 'hp_piutang',
  penerimaan: 'hp_penerimaan',
} as const;

function lsGet<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function lsSet<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // quota exceeded or private mode — silently ignore
  }
}

// ─── Seed / initial data ─────────────────────────────────────────────────────

const seedProjects: Project[] = [
  {
    id: 'P-001',
    nama: 'Gedung H. Syamsuri',
    lokasi: 'Cilegon',
    pic: 'Ahmad Fauzi',
    manager: 'Hendra Kusuma',
    tanggalMulai: '01/08/2026',
    targetSelesai: '31/12/2026',
    budget: 2000000000,
    status: 'Berjalan',
    deskripsi: 'Pembangunan asrama',
  },
  {
    id: 'P-002',
    nama: 'Masjid',
    lokasi: 'Cilegon',
    pic: 'Budi Santoso',
    manager: 'Ridwan Prasetyo',
    tanggalMulai: '01/08/2026',
    targetSelesai: '30/06/2027',
    budget: 5000000000,
    status: 'Berjalan',
    deskripsi: 'Pembangunan masjid',
  },
  {
    id: 'P-003',
    nama: 'Gedung Sekolah',
    lokasi: 'Cilegon',
    pic: 'Siti Rahayu',
    manager: 'Dian Permana',
    tanggalMulai: '05/08/2026',
    targetSelesai: '31/03/2027',
    budget: 3000000000,
    status: 'Persiapan',
    deskripsi: 'Gedung sekolah',
  },
];

const seedSubProjects: SubProject[] = [
  { id: 'P-001.01', projectId: 'P-001', nama: 'Struktur', pic: 'PIC Struktur', budget: 500000000, status: 'Berjalan' },
  { id: 'P-001.02', projectId: 'P-001', nama: 'Dinding', pic: 'PIC Dinding', budget: 250000000, status: 'Berjalan' },
  { id: 'P-001.03', projectId: 'P-001', nama: 'Lantai', pic: 'PIC Finishing', budget: 200000000, status: 'Berjalan' },
  { id: 'P-001.04', projectId: 'P-001', nama: 'Plumbing', pic: 'PIC MEP', budget: 150000000, status: 'Berjalan' },
  { id: 'P-001.05', projectId: 'P-001', nama: 'Electrical', pic: 'PIC MEP', budget: 150000000, status: 'Berjalan' },
  { id: 'P-001.06', projectId: 'P-001', nama: 'Finishing', pic: 'PIC Finishing', budget: 300000000, status: 'Berjalan' },
];

const seedHutang: Hutang[] = [
  {
    id: 'HTG-001',
    projectId: 'P-001',
    subProjectId: 'P-001.02',
    tanggal: '01/08/2026',
    vendor: 'CV ABC',
    kategori: 'Material',
    uraian: 'Hebel & Mortar',
    noInvoice: 'INV-001',
    nilaiTagihan: 50000000,
    totalDibayar: 20000000,
    sisaHutang: 30000000,
    jatuhTempo: '15/08/2026',
    umurHari: 3,
    status: 'Overdue',
    pic: 'Asep',
    approval: 'Approved',
    lampiran: '',
  },
  {
    id: 'HTG-002',
    projectId: 'P-002',
    subProjectId: 'P-002.01',
    tanggal: '03/08/2026',
    vendor: 'PT XYZ',
    kategori: 'Material',
    uraian: 'Besi Beton',
    noInvoice: 'INV-022',
    nilaiTagihan: 100000000,
    totalDibayar: 50000000,
    sisaHutang: 50000000,
    jatuhTempo: '20/08/2026',
    umurHari: 0,
    status: '1-7 Hari',
    pic: 'Budi',
    approval: 'Approved',
    lampiran: '',
  },
  {
    id: 'HTG-003',
    projectId: 'P-002',
    subProjectId: 'P-002.01',
    tanggal: '05/08/2026',
    vendor: 'CV Karya',
    kategori: 'Subkon',
    uraian: 'Bekisting',
    noInvoice: 'INV-023',
    nilaiTagihan: 75000000,
    totalDibayar: 0,
    sisaHutang: 75000000,
    jatuhTempo: '17/08/2026',
    umurHari: 1,
    status: 'Overdue',
    pic: 'Budi',
    approval: 'Approved',
    lampiran: '',
  },
];

const seedPembayaran: PembayaranHutang[] = [
  {
    id: 'PAY-001',
    hutangId: 'HTG-001',
    projectId: 'P-001',
    tanggalPembayaran: '07/08/2026',
    vendor: 'CV ABC',
    nominal: 20000000,
    bankKas: 'BSI',
    metode: 'Transfer',
    noBukti: 'TRF-001',
    lampiran: '',
  },
  {
    id: 'PAY-002',
    hutangId: 'HTG-002',
    projectId: 'P-002',
    tanggalPembayaran: '08/08/2026',
    vendor: 'PT XYZ',
    nominal: 50000000,
    bankKas: 'BSI',
    metode: 'Transfer',
    noBukti: 'TRF-002',
    lampiran: '',
  },
];

const seedPiutang: Piutang[] = [
  {
    id: 'PGT-001',
    projectId: 'P-002',
    subProjectId: '-',
    tanggal: '01/08/2026',
    sumberDana: 'Wakaf',
    pihak: 'Donatur A',
    uraian: 'Wakaf pembangunan',
    nilaiPiutang: 500000000,
    totalDiterima: 200000000,
    sisaPiutang: 300000000,
    jatuhTempo: '30/08/2026',
    umurHari: 0,
    status: 'Belum Jatuh Tempo',
    pic: 'Marketing',
    lampiran: '',
  },
  {
    id: 'PGT-002',
    projectId: 'P-001',
    subProjectId: '-',
    tanggal: '05/08/2026',
    sumberDana: 'Donasi',
    pihak: 'Donatur B',
    uraian: 'Donasi asrama',
    nilaiPiutang: 100000000,
    totalDiterima: 50000000,
    sisaPiutang: 50000000,
    jatuhTempo: '20/08/2026',
    umurHari: 0,
    status: '1-7 Hari',
    pic: 'Marketing',
    lampiran: '',
  },
];

const seedPenerimaan: PenerimaanPiutang[] = [
  {
    id: 'RCV-001',
    piutangId: 'PGT-001',
    projectId: 'P-002',
    tanggalPenerimaan: '06/08/2026',
    pihak: 'Donatur A',
    nominal: 200000000,
    bankKas: 'BSI',
    metode: 'Transfer',
    noBukti: 'TRF-R01',
    lampiran: '',
  },
  {
    id: 'RCV-002',
    piutangId: 'PGT-002',
    projectId: 'P-001',
    tanggalPenerimaan: '09/08/2026',
    pihak: 'Donatur B',
    nominal: 50000000,
    bankKas: 'BSI',
    metode: 'Transfer',
    noBukti: 'TRF-R02',
    lampiran: '',
  },
];

// ─── In-memory stores (initialised from localStorage or seed) ────────────────

export let projectsStore: Project[] = lsGet<Project[]>(LS_KEYS.projects, seedProjects);
export let subProjectsStore: SubProject[] = lsGet<SubProject[]>(LS_KEYS.subProjects, seedSubProjects);
export let hutangStore: Hutang[] = lsGet<Hutang[]>(LS_KEYS.hutang, seedHutang);
export let pembayaranStore: PembayaranHutang[] = lsGet<PembayaranHutang[]>(LS_KEYS.pembayaran, seedPembayaran);
export let piutangStore: Piutang[] = lsGet<Piutang[]>(LS_KEYS.piutang, seedPiutang);
export let penerimaanStore: PenerimaanPiutang[] = lsGet<PenerimaanPiutang[]>(LS_KEYS.penerimaan, seedPenerimaan);

// Seed localStorage on first load (when key is absent)
if (typeof window !== 'undefined') {
  if (localStorage.getItem(LS_KEYS.projects) === null) lsSet(LS_KEYS.projects, seedProjects);
  if (localStorage.getItem(LS_KEYS.subProjects) === null) lsSet(LS_KEYS.subProjects, seedSubProjects);
  if (localStorage.getItem(LS_KEYS.hutang) === null) lsSet(LS_KEYS.hutang, seedHutang);
  if (localStorage.getItem(LS_KEYS.pembayaran) === null) lsSet(LS_KEYS.pembayaran, seedPembayaran);
  if (localStorage.getItem(LS_KEYS.piutang) === null) lsSet(LS_KEYS.piutang, seedPiutang);
  if (localStorage.getItem(LS_KEYS.penerimaan) === null) lsSet(LS_KEYS.penerimaan, seedPenerimaan);
}

// ─── Projects ────────────────────────────────────────────────────────────────

export function getProjects(): Project[] {
  projectsStore = lsGet<Project[]>(LS_KEYS.projects, projectsStore);
  return projectsStore;
}

export function addProject(p: Project): void {
  projectsStore = lsGet<Project[]>(LS_KEYS.projects, projectsStore);
  projectsStore.push(p);
  lsSet(LS_KEYS.projects, projectsStore);
}

export function updateProject(id: string, updates: Partial<Project>): void {
  projectsStore = lsGet<Project[]>(LS_KEYS.projects, projectsStore);
  const idx = projectsStore.findIndex((p) => p.id === id);
  if (idx !== -1) {
    projectsStore[idx] = { ...projectsStore[idx], ...updates };
    lsSet(LS_KEYS.projects, projectsStore);
  }
}

export function deleteProject(id: string): void {
  projectsStore = lsGet<Project[]>(LS_KEYS.projects, projectsStore);
  projectsStore = projectsStore.filter((p) => p.id !== id);
  lsSet(LS_KEYS.projects, projectsStore);
}

// ─── Sub Projects ─────────────────────────────────────────────────────────────

export function getSubProjects(): SubProject[] {
  subProjectsStore = lsGet<SubProject[]>(LS_KEYS.subProjects, subProjectsStore);
  return subProjectsStore;
}

export function getSubProjectsByProject(projectId: string): SubProject[] {
  subProjectsStore = lsGet<SubProject[]>(LS_KEYS.subProjects, subProjectsStore);
  return subProjectsStore.filter((s) => s.projectId === projectId);
}

export function addSubProject(sp: SubProject): void {
  subProjectsStore = lsGet<SubProject[]>(LS_KEYS.subProjects, subProjectsStore);
  subProjectsStore.push(sp);
  lsSet(LS_KEYS.subProjects, subProjectsStore);
}

export function updateSubProject(id: string, updates: Partial<SubProject>): void {
  subProjectsStore = lsGet<SubProject[]>(LS_KEYS.subProjects, subProjectsStore);
  const idx = subProjectsStore.findIndex((s) => s.id === id);
  if (idx !== -1) {
    subProjectsStore[idx] = { ...subProjectsStore[idx], ...updates };
    lsSet(LS_KEYS.subProjects, subProjectsStore);
  }
}

export function deleteSubProject(id: string): void {
  subProjectsStore = lsGet<SubProject[]>(LS_KEYS.subProjects, subProjectsStore);
  subProjectsStore = subProjectsStore.filter((s) => s.id !== id);
  lsSet(LS_KEYS.subProjects, subProjectsStore);
}

// ─── Hutang ───────────────────────────────────────────────────────────────────

export function getHutangList(): Hutang[] {
  hutangStore = lsGet<Hutang[]>(LS_KEYS.hutang, hutangStore);
  return hutangStore;
}

export function addHutang(h: Hutang): void {
  hutangStore = lsGet<Hutang[]>(LS_KEYS.hutang, hutangStore);
  hutangStore.push(h);
  lsSet(LS_KEYS.hutang, hutangStore);
}

export function updateHutang(id: string, updates: Partial<Hutang>): void {
  hutangStore = lsGet<Hutang[]>(LS_KEYS.hutang, hutangStore);
  const idx = hutangStore.findIndex((h) => h.id === id);
  if (idx !== -1) {
    hutangStore[idx] = { ...hutangStore[idx], ...updates };
    lsSet(LS_KEYS.hutang, hutangStore);
  }
}

export function deleteHutang(id: string): void {
  hutangStore = lsGet<Hutang[]>(LS_KEYS.hutang, hutangStore);
  hutangStore = hutangStore.filter((h) => h.id !== id);
  lsSet(LS_KEYS.hutang, hutangStore);
}

// ─── Pembayaran Hutang ────────────────────────────────────────────────────────

export function getPembayaranList(): PembayaranHutang[] {
  pembayaranStore = lsGet<PembayaranHutang[]>(LS_KEYS.pembayaran, pembayaranStore);
  return pembayaranStore;
}

export function addPembayaran(p: PembayaranHutang): void {
  pembayaranStore = lsGet<PembayaranHutang[]>(LS_KEYS.pembayaran, pembayaranStore);
  pembayaranStore.push(p);
  lsSet(LS_KEYS.pembayaran, pembayaranStore);
}

export function updatePembayaran(id: string, updates: Partial<PembayaranHutang>): void {
  pembayaranStore = lsGet<PembayaranHutang[]>(LS_KEYS.pembayaran, pembayaranStore);
  const idx = pembayaranStore.findIndex((p) => p.id === id);
  if (idx !== -1) {
    pembayaranStore[idx] = { ...pembayaranStore[idx], ...updates };
    lsSet(LS_KEYS.pembayaran, pembayaranStore);
  }
}

export function deletePembayaran(id: string): void {
  pembayaranStore = lsGet<PembayaranHutang[]>(LS_KEYS.pembayaran, pembayaranStore);
  pembayaranStore = pembayaranStore.filter((p) => p.id !== id);
  lsSet(LS_KEYS.pembayaran, pembayaranStore);
}

// ─── Piutang ──────────────────────────────────────────────────────────────────

export function getPiutangList(): Piutang[] {
  piutangStore = lsGet<Piutang[]>(LS_KEYS.piutang, piutangStore);
  return piutangStore;
}

export function addPiutang(p: Piutang): void {
  piutangStore = lsGet<Piutang[]>(LS_KEYS.piutang, piutangStore);
  piutangStore.push(p);
  lsSet(LS_KEYS.piutang, piutangStore);
}

export function updatePiutang(id: string, updates: Partial<Piutang>): void {
  piutangStore = lsGet<Piutang[]>(LS_KEYS.piutang, piutangStore);
  const idx = piutangStore.findIndex((p) => p.id === id);
  if (idx !== -1) {
    piutangStore[idx] = { ...piutangStore[idx], ...updates };
    lsSet(LS_KEYS.piutang, piutangStore);
  }
}

export function deletePiutang(id: string): void {
  piutangStore = lsGet<Piutang[]>(LS_KEYS.piutang, piutangStore);
  piutangStore = piutangStore.filter((p) => p.id !== id);
  lsSet(LS_KEYS.piutang, piutangStore);
}

// ─── Penerimaan Piutang ───────────────────────────────────────────────────────

export function getPenerimaanList(): PenerimaanPiutang[] {
  penerimaanStore = lsGet<PenerimaanPiutang[]>(LS_KEYS.penerimaan, penerimaanStore);
  return penerimaanStore;
}

export function addPenerimaan(p: PenerimaanPiutang): void {
  penerimaanStore = lsGet<PenerimaanPiutang[]>(LS_KEYS.penerimaan, penerimaanStore);
  penerimaanStore.push(p);
  lsSet(LS_KEYS.penerimaan, penerimaanStore);
}

export function updatePenerimaan(id: string, updates: Partial<PenerimaanPiutang>): void {
  penerimaanStore = lsGet<PenerimaanPiutang[]>(LS_KEYS.penerimaan, penerimaanStore);
  const idx = penerimaanStore.findIndex((p) => p.id === id);
  if (idx !== -1) {
    penerimaanStore[idx] = { ...penerimaanStore[idx], ...updates };
    lsSet(LS_KEYS.penerimaan, penerimaanStore);
  }
}

export function deletePenerimaan(id: string): void {
  penerimaanStore = lsGet<PenerimaanPiutang[]>(LS_KEYS.penerimaan, penerimaanStore);
  penerimaanStore = penerimaanStore.filter((p) => p.id !== id);
  lsSet(LS_KEYS.penerimaan, penerimaanStore);
}

// ─── Legacy aliases (kept for backward compatibility) ────────────────────────

export const mockProjects = seedProjects;
export const mockSubProjects = seedSubProjects;
export const mockHutang = seedHutang;
export const mockPembayaranHutang = seedPembayaran;
export const mockPiutang = seedPiutang;
export const mockPenerimaanPiutang = seedPenerimaan;

// ─── Options ──────────────────────────────────────────────────────────────────

export const statusProjectOptions: ProjectStatus[] = ['Persiapan', 'Berjalan', 'Ditunda', 'Selesai', 'Dibatalkan'];
export const kategoriHutangOptions: KategoriHutang[] = ['Material', 'Subkon', 'Upah', 'Sewa Alat', 'Operasional', 'Konsultan', 'Lainnya'];
export const sumberDanaOptions: SumberDana[] = ['Donasi', 'Wakaf', 'Internal', 'Hibah', 'Lainnya'];
export const statusTransaksiOptions: HutangStatus[] = ['Belum Lunas', 'Sebagian', 'Lunas', 'Overdue'];
export const metodePembayaranOptions: MetodePembayaran[] = ['Transfer', 'Cash', 'QRIS', 'Lainnya'];
export const bankKasOptions: BankKas[] = ['Kas Proyek', 'BSI', 'Bank Lain'];

// ─── Activity Log (immutable — append-only) ───────────────────────────────────

export type ActivityAction =
  | 'TAMBAH_HUTANG' |'EDIT_HUTANG' |'HAPUS_HUTANG' |'BAYAR_HUTANG' |'TAMBAH_PEMBAYARAN' |'EDIT_PEMBAYARAN' |'HAPUS_PEMBAYARAN' |'TAMBAH_PIUTANG' |'EDIT_PIUTANG' |'HAPUS_PIUTANG' |'TAMBAH_PENERIMAAN' |'EDIT_PENERIMAAN' |'HAPUS_PENERIMAAN' |'TAMBAH_PROJECT' |'EDIT_PROJECT' |'HAPUS_PROJECT' |'TAMBAH_SUBPROJECT' |'EDIT_SUBPROJECT' |'HAPUS_SUBPROJECT';

export interface ActivityLog {
  id: string;
  timestamp: string;
  action: ActivityAction;
  module: string;
  description: string;
  refId: string;
}

const LS_KEY_LOG = 'hp_activity_log';

export function getActivityLogs(): ActivityLog[] {
  return lsGet<ActivityLog[]>(LS_KEY_LOG, []);
}

export function addActivityLog(entry: Omit<ActivityLog, 'id' | 'timestamp'>): void {
  const logs = getActivityLogs();
  const newLog: ActivityLog = {
    id: `LOG-${String(logs.length + 1).padStart(4, '0')}`,
    timestamp: new Date().toISOString(),
    ...entry,
  };
  logs.push(newLog);
  lsSet(LS_KEY_LOG, logs);
}